import React, { useState, useEffect } from 'react';
import { patientService } from '../../services/patientService';
import { Patient, ApiResponse } from '../../types';
import ImageUpload from '../molecules/ImageUpload';

interface PatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient?: Patient | null;
  onSuccess: () => void;
}

interface PatientFormData {
  name: string;
  species: string;
  breed: string;
  gender: 'MALE' | 'FEMALE';
  birthDate: string;
  color: string;
  microchipId: string;
  ownerId: string;
}

const PatientModal: React.FC<PatientModalProps> = ({
  isOpen,
  onClose,
  patient,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | undefined>();
  const [formData, setFormData] = useState<PatientFormData>({
    name: '',
    species: '',
    breed: '',
    gender: 'MALE',
    birthDate: '',
    color: '',
    microchipId: '',
    ownerId: '',
  });

  // Reset form when modal opens/closes or patient changes
  useEffect(() => {
    if (isOpen) {
      if (patient) {
        // Edit mode - populate form with patient data
        setFormData({
          name: patient.name,
          species: patient.species,
          breed: patient.breed,
          gender: patient.gender,
          birthDate: patient.birthDate ? patient.birthDate.split('T')[0] : '',
          color: patient.color || '',
          microchipId: patient.microchipId || '',
          ownerId: patient.ownerId,
        });
        setImagePreview(patient.photoUrl);
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          species: '',
          breed: '',
          gender: 'MALE',
          birthDate: '',
          color: '',
          microchipId: '',
          ownerId: '',
        });
        setImagePreview(undefined);
      }
      setSelectedImage(null);
      setError(null);
    }
  }, [isOpen, patient]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (file: File | null, preview?: string) => {
    setSelectedImage(file);
    setImagePreview(preview);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.species || !formData.breed || !formData.ownerId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const patientData = {
        ...formData,
        birthDate: formData.birthDate || undefined,
        color: formData.color || undefined,
        microchipId: formData.microchipId || undefined,
      };

      let response: ApiResponse<Patient>;

      if (patient) {
        // Update existing patient
        response = await patientService.updatePatient({ id: patient.id, ...patientData });
      } else {
        // Create new patient
        response = await patientService.createPatient(patientData);
      }

      if (response.success && response.data) {
        // Handle image upload if a new image was selected
        if (selectedImage) {
          try {
            await patientService.uploadPatientPhoto(response.data.id, selectedImage);
          } catch (uploadError: any) {
            console.warn('Image upload failed:', uploadError);
            // Don't fail the entire operation if image upload fails
          }
        }

        onSuccess();
        onClose();
      } else {
        setError(response.message || 'Failed to save patient');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving patient');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">
            {patient ? 'Edit Patient' : 'Add New Patient'}
          </h2>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
          >
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient Photo */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Patient Photo</span>
            </label>
            <ImageUpload
              value={imagePreview}
              onChange={handleImageChange}
              placeholder="Upload patient photo"
              disabled={loading}
            />
          </div>

          {/* Patient Name */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Patient Name *</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="Enter patient name"
              required
            />
          </div>

          {/* Species and Gender */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Species *</span>
              </label>
              <select
                name="species"
                value={formData.species}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                required
              >
                <option value="">Select species</option>
                <option value="dog">Dog</option>
                <option value="cat">Cat</option>
                <option value="bird">Bird</option>
                <option value="rabbit">Rabbit</option>
                <option value="hamster">Hamster</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Gender *</span>
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                required
              >
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </select>
            </div>
          </div>

          {/* Breed */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Breed *</span>
            </label>
            <input
              type="text"
              name="breed"
              value={formData.breed}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="Enter breed"
              required
            />
          </div>

          {/* Birth Date and Color */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Birth Date</span>
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleInputChange}
                className="input input-bordered w-full"
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Color</span>
              </label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                placeholder="Enter color"
              />
            </div>
          </div>

          {/* Microchip ID and Owner ID */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Microchip ID</span>
            </label>
            <input
              type="text"
              name="microchipId"
              value={formData.microchipId}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="Enter microchip ID"
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Owner ID *</span>
            </label>
            <input
              type="text"
              name="ownerId"
              value={formData.ownerId}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="Enter owner ID"
              required
            />
          </div>

          {/* Actions */}
          <div className="modal-action">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading && <span className="loading loading-spinner" />}
              {loading ? 'Saving...' : patient ? 'Update Patient' : 'Add Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientModal;